// ─── helpers ────────────────────────────────────────────────────────────────

function pad2(n) { return String(n).padStart(2, '0') }

/**
 * Formata data BR ignorando nulos e o epoch Unix (01/01/1970, 31/12/1969, etc.)
 * O Firebird retorna NULL de datetime como 1969-12-31 em alguns drivers.
 */
function fmtData(valor) {
  if (!valor) return '—'
  const d = new Date(valor)
  if (isNaN(+d)) return '—'
  if (d.getFullYear() < 1980) return '—'
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`
}

function fmtDataHora(valor) {
  if (!valor) return '—'
  const d = new Date(valor)
  if (isNaN(+d)) return '—'
  if (d.getFullYear() < 1980) return '—'
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

function fmtMoeda(valor) {
  const n = Number(valor)
  if (!n && n !== 0) return 'R$ 0,00'
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function fmtQtd(valor, casas = 3) {
  const n = Number(valor)
  if (isNaN(n)) return '0'
  return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
}

// ────────────────────────────────────────────────────────────────────────────

/**
 * Gera e imprime o Espelho da Compra em formato HTML Premium.
 * Usa thead/tfoot para repetir header e footer em cada página impressa.
 *
 * @param {Object} dados           - Objeto contendo { principal, itens, despesas, parcelamento }
 * @param {string} usuarioLogado   - Nome do usuário logado
 */
export async function imprimirEspelhoCompra(dados, usuarioLogado = '') {
  const { principal, itens, despesas, parcelamento } = dados

  const nomeEmpresa    = (window.localStorage.getItem('nomeempresa') || 'CECWeb').trim()
  const dataImpressao  = fmtDataHora(new Date())
  const tituloRelatorio = `ESPELHO DA COMPRA Nº ${principal.ID}`
  const emissor        = usuarioLogado || 'SISTEMA'

  // ─── CSS ──────────────────────────────────────────────────────────────────
  // Estratégia de paginação:
  // • thead/tfoot repetem naturalmente em cada página impressa (suporte universal)
  // • @page define margens e numeração de páginas via @bottom-right (Chrome 85+)
  // • counter(page) / counter(pages) funciona em Chrome/Edge para @page boxes
  const css = `<style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      color: #1e293b;
      background: #fff;
    }

    /* ── Tabela-raiz que controla header/footer por página ── */
    #rpt-root {
      width: 100%;
      border-collapse: collapse;
      border-spacing: 0;
    }

    /* ── HEADER – repete em cada página impressa ── */
    #rpt-root thead td {
      padding: 0 0 8px 0;
      border-bottom: 2px solid #20a8d8;
    }
    .rpt-header-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 0 4px 0;
    }
    .rpt-h-empresa { font-weight: 700; font-size: 12px; color: #1e293b; }
    .rpt-h-right   { text-align: right; }
    .rpt-h-titulo  { font-weight: 700; font-size: 12px; color: #20a8d8; }
    .rpt-h-sub     { font-size: 9px; color: #64748b; margin-top: 2px; }

    /* ── FOOTER – repete em cada página impressa ── */
    #rpt-root tfoot td {
      padding: 6px 0 0 0;
      border-top: 1px solid #e2e8f0;
      font-size: 9px;
      color: #94a3b8;
    }
    .rpt-footer-inner {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    /* ── Corpo do relatório ── */
    #rpt-root tbody td { padding: 10px 0 0 0; vertical-align: top; }

    /* ── Seções do espelho ── */
    .mirror-section {
      margin-bottom: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
      background: #fff;
    }
    .mirror-section-title {
      background: #f8fafc;
      padding: 7px 13px;
      font-weight: 700;
      color: #1e293b;
      border-bottom: 1px solid #e2e8f0;
      text-transform: uppercase;
      font-size: 10px;
    }
    .mirror-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      padding: 10px 13px;
    }
    .mirror-field  { display: flex; flex-direction: column; }
    .mirror-label  { font-size: 8px; color: #64748b; text-transform: uppercase; font-weight: 700; margin-bottom: 2px; letter-spacing: 0.4px; }
    .mirror-value  { font-size: 11px; color: #0f172a; font-weight: 500; }

    .mirror-table  { width: 100%; border-collapse: collapse; font-size: 10px; }
    .mirror-table th {
      background: #f1f5f9; padding: 6px 8px;
      text-align: left; border-bottom: 2px solid #e2e8f0;
      color: #475569; font-weight: 700; text-transform: uppercase; font-size: 9px;
    }
    .mirror-table td { padding: 6px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }

    .mirror-lote-container {
      padding: 5px 8px 8px 28px;
      border-left: 4px solid #20a8d8;
      margin: 2px 8px 8px 10px;
      background: #fcfdfe;
      border-radius: 0 5px 5px 0;
    }
    .mirror-lote-title {
      font-size: 8px; font-weight: 700; color: #20a8d8;
      margin-bottom: 4px; text-transform: uppercase;
    }

    .mirror-badge {
      padding: 2px 7px; border-radius: 50px;
      font-size: 8px; font-weight: 700; text-transform: uppercase; display: inline-block;
    }
    .badge-success { background: #dcfce7; color: #166534; }
    .badge-warning { background: #fef9c3; color: #854d0e; }
    .badge-primary { background: #e0f2fe; color: #20a8d8; }

    .text-right  { text-align: right; }
    .text-center { text-align: center; }
    .font-bold   { font-weight: 700; }
    .text-primary { color: #0369a1; }
    .total-row   { background: #f8fafc !important; font-weight: 700; border-top: 2px solid #e2e8f0; }
    .total-label { font-size: 10px; color: #475569; }

    /* ── Impressão ── */
    @page {
      size: A4 portrait;
      margin: 12mm 12mm 12mm 12mm;
    }
    @media print {
      body  { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      /* NÃO usar break-inside: avoid nas seções grandes (itens/lotes).
         Isso causava o header sozinho na pág 1 por empurrar todo o conteúdo.
         Apenas o bloco item+lote é mantido junto via .item-group. */
      .item-group           { break-inside: avoid; }
      .mirror-lote-container { break-inside: avoid; }
    }
  </style>`

  // ─── THEAD (header repetido) ───────────────────────────────────────────
  const theadHtml = `
    <thead>
      <tr>
        <td>
          <div class="rpt-header-inner">
            <span class="rpt-h-empresa">${nomeEmpresa}</span>
            <div class="rpt-h-right">
              <div class="rpt-h-titulo">${tituloRelatorio}</div>
              <div class="rpt-h-sub">Impressão: ${dataImpressao}&nbsp;&nbsp;|&nbsp;&nbsp;Emissor: ${emissor}</div>
            </div>
          </div>
        </td>
      </tr>
    </thead>`

  // ─── TFOOT (footer repetido) ───────────────────────────────────────────
  const tfootHtml = `
    <tfoot>
      <tr>
        <td>
          <div class="rpt-footer-inner">
            <span>CECWeb - Sistema de Gestão Industrial e Comercial</span>
            <span>Emissor: <strong>${emissor}</strong></span>
            <span><!-- numeração via @page no Chrome / cabeçalho do browser nos demais --></span>
          </div>
        </td>
      </tr>
    </tfoot>`

  // ─── SEÇÃO: Identificação ─────────────────────────────────────────────
  const situacaoBadge = principal.OP_STATUS === '2'
    ? '<span class="mirror-badge badge-success">FINALIZADO</span>'
    : '<span class="mirror-badge badge-warning">EM CADASTRO</span>'

  const secaoIdentificacao = `
    <div class="mirror-section">
      <div class="mirror-section-title">Identificação do Pedido de Compra</div>
      <div class="mirror-grid">
        <div class="mirror-field">
          <span class="mirror-label">ID / Controle</span>
          <span class="mirror-value font-bold text-primary">${principal.ID}</span>
        </div>
        <div class="mirror-field">
          <span class="mirror-label">Documento / Externo</span>
          <span class="mirror-value font-bold">${principal.NUMERO_EXTERNO || '—'}</span>
        </div>
        <div class="mirror-field">
          <span class="mirror-label">Data Emissão</span>
          <span class="mirror-value">${fmtData(principal.DT_PEDIDO)}</span>
        </div>
        <div class="mirror-field">
          <span class="mirror-label">Situação</span>
          <span class="mirror-value">${situacaoBadge}</span>
        </div>

        <div class="mirror-field" style="grid-column:span 2">
          <span class="mirror-label">Fornecedor</span>
          <span class="mirror-value font-bold">${principal.FORNECEDOR_ID} - ${principal.FORNECEDOR_NOME || '—'}</span>
        </div>
        <div class="mirror-field">
          <span class="mirror-label">CNPJ Fornecedor</span>
          <span class="mirror-value">${principal.FORNECEDOR_CNPJ || '—'}</span>
        </div>
        <div class="mirror-field">
          <span class="mirror-label">Cidade/UF</span>
          <span class="mirror-value">${[principal.FORNECEDOR_CIDADE, principal.FORNECEDOR_UF].filter(Boolean).join(' / ') || '—'}</span>
        </div>

        <div class="mirror-field" style="grid-column:span 2">
          <span class="mirror-label">Comprador</span>
          <span class="mirror-value">${principal.COMPRADOR_NOME || '—'}</span>
        </div>
        <div class="mirror-field">
          <span class="mirror-label">Tipo Pedido</span>
          <span class="mirror-value">
            <span class="mirror-badge badge-primary">${principal.OP_TIPO_PEDIDO === 'I' ? 'INDÚSTRIA' : 'ALMOXARIFADO'}</span>
          </span>
        </div>
        <div class="mirror-field">
          <span class="mirror-label">Incoterms / Estoque</span>
          <span class="mirror-value">${principal.OP_INCOTERMS || 'FOB'} / ${principal.OP_GERA_ESTOQUE === 'S' ? 'GERA' : 'NÃO GERA'}</span>
        </div>
      </div>
    </div>`

  // ─── SEÇÃO: Itens e Lotes ─────────────────────────────────────────────
  const totalPeso = itens.reduce((s, i) => s + (Number(i.NR_PESO_BRUTO) || 0), 0)
  const totalConf = itens.reduce((s, i) => s + (Number(i.QUANT_CONFERENCIA) || 0), 0)

  const linhasItens = itens.map(item => {
    const linhasLotes = item.lotes && item.lotes.length > 0 ? `
      <tr>
        <td colspan="7" style="padding:0">
          <div class="mirror-lote-container">
            <div class="mirror-lote-title">Rastreabilidade (Lotes)</div>
            <table class="mirror-table" style="border:1px solid #f1f5f9;border-radius:4px">
              <thead>
                <tr>
                  <th style="font-size:8px">Lote</th>
                  <th style="font-size:8px">Fabricação</th>
                  <th style="font-size:8px">Validade</th>
                  <th style="font-size:8px" class="text-right">Quantidade</th>
                  <th style="font-size:8px" class="text-right">Peças</th>
                  <th style="font-size:8px">Observação</th>
                </tr>
              </thead>
              <tbody>
                ${item.lotes.map(lote => `
                  <tr>
                    <td class="font-bold" style="color:#475569">${lote.LOTE || '—'}</td>
                    <td>${fmtData(lote.DATA_FABRICACAO)}</td>
                    <td>${fmtData(lote.DATA_VALIDADE)}</td>
                    <td class="text-right font-bold">${fmtQtd(lote.QUANTIDADE, 3)}</td>
                    <td class="text-right">${lote.PECAS || 0}</td>
                    <td style="font-style:italic;color:#94a3b8">${lote.OBSERVACAO || ''}</td>
                  </tr>`).join('')}
              </tbody>
            </table>
          </div>
        </td>
      </tr>` : ''

    return `
      <tbody class="item-group">
        <tr>
          <td>${item.PRODUTO_ID}</td>
          <td class="font-bold">${item.PRODUTO_NOME || '—'}</td>
          <td class="text-center">${item.UNIDADE || 'UN'}</td>
          <td class="text-right">${fmtQtd(item.NR_PESO_BRUTO, 3)}</td>
          <td class="text-right font-bold text-primary">${fmtQtd(item.QUANT_CONFERENCIA, 3)}</td>
          <td class="text-right">${fmtMoeda(item.VL_UNITARIO_FOB)}</td>
          <td class="text-right font-bold">${fmtMoeda(item.VL_TOTAL_FOB)}</td>
        </tr>
        ${linhasLotes}
      </tbody>`
  }).join('')

  const secaoItens = `
    <div class="mirror-section">
      <div class="mirror-section-title">Itens e Detalhamento de Lotes</div>
      <table class="mirror-table">
        <thead>
          <tr>
            <th style="width:55px">Cód.</th>
            <th>Descrição do Produto</th>
            <th style="width:38px" class="text-center">UN</th>
            <th style="width:82px" class="text-right">Qtd Pedida</th>
            <th style="width:82px" class="text-right">Qtd Conf.</th>
            <th style="width:92px" class="text-right">Preço Unit.</th>
            <th style="width:102px" class="text-right">Total FOB</th>
          </tr>
        </thead>
        ${linhasItens}
        <tbody>
          <tr class="total-row">
            <td colspan="3" class="text-right total-label">TOTAIS DO PEDIDO:</td>
            <td class="text-right">${fmtQtd(totalPeso, 3)}</td>
            <td class="text-right text-primary">${fmtQtd(totalConf, 3)}</td>
            <td></td>
            <td class="text-right" style="font-size:12px;color:#0369a1">${fmtMoeda(principal.VL_PEDIDO)}</td>
          </tr>
        </tbody>
      </table>
    </div>`

  // ─── SEÇÃO: Logística & Financeiro ────────────────────────────────────
  const linhasParc = parcelamento.length === 0
    ? '<tr><td colspan="4" class="text-center" style="padding:14px;color:#94a3b8">Nenhum parcelamento gerado</td></tr>'
    : parcelamento.map(p => `
        <tr>
          <td class="text-center">${p.PARCELA}</td>
          <td>${fmtData(p.DATA_VENCIMENTO)}</td>
          <td class="text-right font-bold">${fmtMoeda(p.VALOR)}</td>
          <td>${p.DOCUMENTO || '—'}</td>
        </tr>`).join('')

  const secaoLogisticaFinanceiro = `
    <div style="display:grid;grid-template-columns:1.2fr 1fr;gap:12px">
      <div class="mirror-section">
        <div class="mirror-section-title">Logística e Conferência</div>
        <div class="mirror-grid" style="grid-template-columns:1fr 1fr;padding:10px 13px">
          <div class="mirror-field" style="grid-column:span 2">
            <span class="mirror-label">Transportadora</span>
            <span class="mirror-value font-bold">${principal.TRANSPORTADORA_NOME || '—'}</span>
          </div>
          <div class="mirror-field">
            <span class="mirror-label">Placa Veículo</span>
            <span class="mirror-value">${principal.PLACA || '—'}</span>
          </div>
          <div class="mirror-field">
            <span class="mirror-label">Nota Fiscal</span>
            <span class="mirror-value font-bold">${principal.NOTA_FISCAL || '—'}</span>
          </div>
          <div class="mirror-field">
            <span class="mirror-label">Início Conferência</span>
            <span class="mirror-value">${fmtDataHora(principal.DT_CONFERENCIA_INICIO)}</span>
          </div>
          <div class="mirror-field">
            <span class="mirror-label">Fim Conferência</span>
            <span class="mirror-value">${fmtDataHora(principal.DT_CONFERENCIA_FIM)}</span>
          </div>
          <div class="mirror-field">
            <span class="mirror-label">Conferente</span>
            <span class="mirror-value font-bold">${principal.USUARIO_CONFERENCIA || '—'}</span>
          </div>
          <div class="mirror-field">
            <span class="mirror-label">Temperaturas (C / A / P)</span>
            <span class="mirror-value">${principal.NR_TEMPERATURA_CARGA || 0}º / ${principal.NR_TEMPERATURA_AMB || 0}º / ${principal.NR_TEMPERATURA || 0}º</span>
          </div>
        </div>
      </div>

      <div class="mirror-section">
        <div class="mirror-section-title">Agenda Financeira</div>
        <table class="mirror-table">
          <thead>
            <tr>
              <th style="width:38px">Parc.</th>
              <th>Vencimento</th>
              <th class="text-right">Valor</th>
              <th>Doc.</th>
            </tr>
          </thead>
          <tbody>${linhasParc}</tbody>
        </table>
      </div>
    </div>`

  // ─── SEÇÃO: Observações (condicional) ─────────────────────────────────
  const secaoObs = (principal.OBS || principal.OBS_FISCO) ? `
    <div class="mirror-section">
      <div class="mirror-section-title">Observações Adicionais</div>
      <div style="padding:10px 13px;font-size:10px;color:#475569;white-space:pre-wrap;line-height:1.5">
        ${principal.OBS ? `<div style="margin-bottom:5px"><strong>Geral:</strong> ${principal.OBS}</div>` : ''}
        ${principal.OBS_FISCO ? `<div><strong>Fisco:</strong> ${principal.OBS_FISCO}</div>` : ''}
      </div>
    </div>` : ''

  // ─── HTML FINAL ────────────────────────────────────────────────────────
  // Estratégia de paginação:
  // thead/tfoot são os mecanismos mais confiáveis e universais para header/footer por página.
  // O browser repetirá automaticamente thead e tfoot em cada quebra de página.
  const fullHtml = `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <title>${tituloRelatorio}</title>
  ${css}
</head>
<body>
  <table id="rpt-root">
    ${theadHtml}
    ${tfootHtml}
    <tbody>
      <tr><td style="padding-top:10px;vertical-align:top">${secaoIdentificacao}</td></tr>
      <tr><td style="padding-top:0;vertical-align:top">${secaoItens}</td></tr>
      <tr><td style="padding-top:0;vertical-align:top">${secaoLogisticaFinanceiro}</td></tr>
      ${(principal.OBS || principal.OBS_FISCO) ? `<tr><td style="padding-top:0;vertical-align:top">${secaoObs}</td></tr>` : ''}
    </tbody>
  </table>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 250);
    });
  </script>
</body>
</html>`

  const w = window.open('', '_blank')
  if (!w) return
  w.document.open('text/html', 'replace')
  w.document.write(fullHtml)
  w.document.close()
  w.focus()
}



// WEBPACK FOOTER //
// ./src/utils/RelatorioCompraHTML.js